import { toSnapshot } from '@dinero.js/core';
import { CAD, EUR, GBP, USD } from '@dinero.js/currencies';
import { allocate, dinero, toFormat, toUnit } from 'dinero.js';
import { CurrencyCode } from '../types/CurrencyCode';
const SupportedCurrency = {
    USD: USD,
    CAD: CAD,
    EUR: EUR,
    GBP: GBP,
};
export const CountryCurrencyMap = {
    US: CurrencyCode.USD,
    CA: CurrencyCode.CAD,
    GB: CurrencyCode.GBP,
    UK: CurrencyCode.GBP,
};
// converts decimal amount to minor units - 11.11 -> 1111
export const toMinorUnits = (amount, currency, scale = 1) => {
    const _currency = SupportedCurrency[currency];
    const factor = Math.pow(_currency.base, _currency.exponent) || scale;
    const minorUnitAmount = Math.round(amount * factor);
    return minorUnitAmount;
};
// converts minor units to decimal amount - 1111 -> 11.11
export const toUnits = (minorUnits, currencyCode) => {
    const d = dinero({
        amount: minorUnits,
        currency: SupportedCurrency[currencyCode],
    });
    return toUnit(d);
};
export const toUnitsEpic = ({ units, partialUnits }, currencyCode) => Number(units) + toUnits(partialUnits, currencyCode);
export const toMinorUnitsEpic = ({ units, partialUnits }, currencyCode) => toMinorUnits(Number(units), currencyCode) + partialUnits;
export const getDineroObject = (amount, currency, scale) => {
    const minorUnits = toMinorUnits(amount, currency, scale);
    return dinero({ amount: minorUnits, currency: SupportedCurrency[currency] });
};
export const toAdyenMoney = (amount, currency) => {
    const d = getDineroObject(amount, currency);
    const snapshot = toSnapshot(d);
    return {
        value: snapshot.amount,
        currency: snapshot.currency,
    };
};
// amount: minorUnits
export const toIntlFormat = ({ amount, currencyCode }, locale, options = {}) => {
    let d;
    try {
        d = dinero({
            amount: Math.round(amount),
            currency: SupportedCurrency[currencyCode],
        });
    }
    catch (err) {
        console.error('Failed to parse amount with dinero', amount, err);
        return '$invalid';
    }
    function transformer({ amount, currency }) {
        return amount.toLocaleString(locale.replace('_', '-'), Object.assign(Object.assign({}, options), { style: 'currency', currency: currency.code }));
    }
    return toFormat(d, transformer);
};
export const toIntlFormatEpic = (amount, currencyCode, locale) => toIntlFormat({ amount: toMinorUnitsEpic(amount, currencyCode), currencyCode }, locale);
export const calculateSplitFromDecimal = (shareDecimal, amountDecimal, currencyCode) => {
    const d = getDineroObject(amountDecimal, currencyCode);
    const shareMinorUnits = toMinorUnits(shareDecimal, currencyCode);
    return calculateSplit(d, shareMinorUnits);
};
export const calculateSplitFromMinorUnits = (shareMinorUnits, amountMinorUnits, currencyCode) => {
    if (!Number.isInteger(shareMinorUnits)) {
        throw new Error('share must be an integer');
    }
    const d = dinero({
        amount: amountMinorUnits,
        currency: SupportedCurrency[currencyCode],
    });
    return calculateSplit(d, shareMinorUnits);
};
const calculateSplit = (dineroObject, shareMinorUnits) => {
    const remainder = 10000 - shareMinorUnits;
    const roundTo = 'agency';
    if (roundTo === 'agency') {
        const split = allocate(dineroObject, [shareMinorUnits, remainder]);
        return [
            Object.assign(Object.assign({}, toSnapshot(split[0])), { units: toUnit(split[0]) }),
            Object.assign(Object.assign({}, toSnapshot(split[1])), { units: toUnit(split[1]) }),
        ];
    }
    else {
        const split = allocate(dineroObject, [remainder, shareMinorUnits]);
        return [
            Object.assign(Object.assign({}, toSnapshot(split[1])), { units: toUnit(split[1]) }),
            Object.assign(Object.assign({}, toSnapshot(split[0])), { units: toUnit(split[0]) }),
        ];
    }
};
export const unitsToEpicMoney = (units, currencyCode, isMinorUnits = false) => {
    const minor = isMinorUnits ? units : toMinorUnits(units, currencyCode);
    const partial = minor % 100 | 0;
    const whole = toUnits((minor - partial) | 0, currencyCode);
    return {
        partial,
        whole,
    };
};
//# sourceMappingURL=currency.js.map